if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
        this.pickupLocationChangeHandler = () => this.refreshPickupAvailability();
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'quick-add') {
            return;
          }

          // If another UI changed cart quantities (drawer/cart page), mirror it in this card immediately.
          if (event?.cartData) {
            this.syncInputFromCartData(event.cartData);
            return;
          }
          // Fallback to legacy refresh path when cart payload is not provided.
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
        window.duplinPickupGuard?.observe(this, () => this.refreshPickupAvailability());
        document.addEventListener('duplin:pickup-location-changed', this.pickupLocationChangeHandler);
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        window.duplinPickupGuard?.unobserve(this);
        document.removeEventListener('duplin:pickup-location-changed', this.pickupLocationChangeHandler);
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      get activeVariantId() {
        const activeInput = this.querySelector('quantity-input input.quantity__input');
        return activeInput?.dataset?.index || this.dataset.index;
      }

      applyPickupAvailability(result) {
        const quantityInput = this.querySelector('quantity-input');
        const inputEl = quantityInput?.querySelector('input.quantity__input');
        if (!inputEl) return;

        if (result?.shouldCheck && Number.isFinite(result.availableQuantity)) {
          inputEl.dataset.pickupMax = String(result.availableQuantity);
        } else {
          delete inputEl.dataset.pickupMax;
        }
        window.duplinPickupGuard?.renderMessage(this, result);
        quantityInput?.validateQtyRules?.();
      }

      async refreshPickupAvailability(forceCartRefresh = false) {
        if (!window.duplinPickupGuard) return true;
        const inputEl = this.input;
        const variantId = this.activeVariantId;
        if (!inputEl || !variantId) return true;

        try {
          const currentQuantity = Number.parseInt(inputEl.dataset.cartQuantity || inputEl.value || '0', 10) || 0;
          const step = Number.parseInt(inputEl.step || '1', 10) || 1;
          const result = await window.duplinPickupGuard.check({
            element: this,
            variantId,
            currentQuantity,
            targetQuantity: currentQuantity + step,
            forceCartRefresh,
          });
          this.applyPickupAvailability(result);
          return result.available;
        } catch (error) {
          console.error('Unable to load PLP pickup availability:', error);
          this.applyPickupAvailability(window.duplinPickupGuard.getValidationErrorResult());
          return false;
        }
      }

      async validatePickupQuantity(variantId, targetQuantity, currentQuantity) {
        if (!window.duplinPickupGuard || targetQuantity <= currentQuantity) return true;
        try {
          const result = await window.duplinPickupGuard.check({
            element: this,
            variantId,
            currentQuantity,
            targetQuantity,
            forceCartRefresh: true,
          });
          this.applyPickupAvailability(result);
          return result.available;
        } catch (error) {
          console.error('Unable to validate PLP pickup inventory:', error);
          this.applyPickupAvailability(window.duplinPickupGuard.getValidationErrorResult());
          return false;
        }
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          // fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
          // .then((response) => response.text())
          // .then((responseText) => {
          //   console.log("onCartUpdate ::",this.sectionId);
          //   const html = new DOMParser().parseFromString(responseText, 'text/html');
          //   const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
          //   if (sourceQty) {
          //     this.innerHTML = sourceQty.innerHTML;
          //   }
          //   resolve();
          // })
          // .catch((e) => {
          //   //console.error(e);
          //   reject(e);
          // });

          resolve();
        });
      }

      syncInputFromCartData(cartData) {
        const quantityInput = this.querySelector('quantity-input');
        const inputEl = quantityInput?.querySelector('input.quantity__input');
        if (!inputEl) return;

        const variantId = this.activeVariantId;
        if (!variantId) return;

        const quantity = this.getQuantityFromState(cartData, variantId);
        inputEl.value = quantity;
        inputEl.setAttribute('value', quantity);
        inputEl.setAttribute('data-cart-quantity', quantity);

        if (typeof quantityInput.validateQtyRules === 'function') {
          quantityInput.validateQtyRules();
        }
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

     // Assuming this is part of a custom element or class (e.g., QuickAddBulk)

      // --- START: CLASS/COMPONENT METHODS ---

      /**
       * Finds the quantity input element and extracts the data-collection attribute.
       * @param {string} variantId - The variant ID.
       * @returns {string} The value of the data-collection attribute, or an empty string.
       */

      getCollectionData(variantId) {
        const inputSelector = `input.quantity__input[data-quantity-variant-id="${variantId}"]`;
        const inputEl = this.closest('.product-card-wrapper')?.querySelector(inputSelector);
        if (inputEl) {
          const collectionValue = inputEl.getAttribute('data-collection');
          return collectionValue ? collectionValue.trim() : '';
        }
        return '';
      }

       getType(variantId) {
        const qty_p = `input.quantity__input[data-quantity-variant-id="${variantId}"]`;
        const inputElf = this.closest('.product-card-wrapper')?.querySelector(qty_p);
        if (inputElf) {
          const type_value = inputElf.getAttribute('data-type');
          return type_value ? type_value.trim() : '';
        }
        return '';
      }

      async addItemWithProperties(variantId, quantity) {
        
        const collectionData = this.getCollectionData(variantId);
        const typedata = this.getType(variantId);

          const dynamicProperties = {
          // 'First-Time-Add': 'true', // Example static property
          };
          if (typedata) {
            dynamicProperties['_type'] = typedata; 
          }

          if (collectionData) {
              dynamicProperties['_collections'] = collectionData; 
          }
          if (window.customer_id) {
              dynamicProperties['_customer_id'] = String(window.customer_id);
          }
        
        const body = JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: dynamicProperties, 
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        const response = await fetch(`${routes.cart_add_url}`, { 
          ...fetchConfig(), 
          ...{ body } 
        });
        
        if (!response.ok) {
          throw new Error(`Failed to add item ${variantId} with properties: ${await response.text()}`);
        }
        return response.text();
      }

      getInputForVariant(variantId) {
        const inputSelector = `input.quantity__input[data-quantity-variant-id="${variantId}"]`;
        return this.closest('.product-card-wrapper')?.querySelector(inputSelector) || null;
      }

      getCurrentCartQuantity(variantId) {
        const inputEl = this.getInputForVariant(variantId);
        const currentQty = parseInt(inputEl?.getAttribute('data-cart-quantity') || '0', 10);
        return Number.isNaN(currentQty) ? 0 : currentQty;
      }

      getQuantityFromState(parsedState, variantId) {
        const targetVariantId = parseInt(variantId, 10);
        if (Array.isArray(parsedState?.items)) {
          const matchedItem = parsedState.items.find((item) => {
            const itemVariantId = parseInt(item.variant_id ?? item.id, 10);
            return itemVariantId === targetVariantId;
          });
          if (matchedItem) {
            return parseInt(matchedItem.quantity || 0, 10);
          }
        }

        // /cart/add.js can return a single line-item payload.
        if (parseInt(parsedState?.id, 10) === targetVariantId) {
          return parseInt(parsedState?.quantity || 0, 10);
        }

        return 0;
      }

      async updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');
        const ids = Object.keys(items);
        const shouldOpenCartDrawer = ids.some((variantId) => {
          const targetQuantity = Number.parseInt(items[variantId], 10) || 0;
          return targetQuantity > this.getCurrentCartQuantity(variantId);
        });

        for (const variantId of ids) {
          const targetQuantity = Number.parseInt(items[variantId], 10) || 0;
          const currentQuantity = this.getCurrentCartQuantity(variantId);
          const canUpdate = await this.validatePickupQuantity(variantId, targetQuantity, currentQuantity);
          if (!canUpdate) {
            const inputEl = this.getInputForVariant(variantId);
            if (inputEl) {
              inputEl.value = String(currentQuantity);
              inputEl.setAttribute('value', String(currentQuantity));
              inputEl.closest('quantity-input')?.validateQtyRules?.();
            }
            this.selectProgressBar().classList.add('hidden');
            this.setRequestStarted(false);
            return;
          }
        }

        const updatesForBulk = {};
        const addsForProperties = {};
        ids.forEach(variantId => {
          const quantity = parseInt(items[variantId], 10);
          const currentQuantity = this.getCurrentCartQuantity(variantId);
          
          // Preserve line item properties when adding from 0 -> N.
          if (currentQuantity === 0 && quantity > 0) {
            addsForProperties[variantId] = quantity; 
          } else {
            updatesForBulk[variantId] = quantity; 
          }
        });


        let state = '';
        let updatesPromise = Promise.resolve(''); 
        
        // 1. /cart/add.js calls (for items needing properties)
        let propertyAddsPromise = Promise.resolve();
        if (Object.keys(addsForProperties).length > 0) {
            propertyAddsPromise = Promise.all(
              Object.keys(addsForProperties).map(variantId => 
                this.addItemWithProperties(variantId, addsForProperties[variantId])
              )
            );
        }

        // 2. /cart/update.js call (for quantity changes/removals)
        if (Object.keys(updatesForBulk).length > 0) {
          const body = JSON.stringify({
            updates: updatesForBulk,
            sections: this.getSectionsToRender().map((section) => section.section),
            sections_url: this.getSectionsUrl(),
          });

         // console.log(body); 
          updatesPromise = fetch(`${routes.cart_update_url}`, { 
            ...fetchConfig(), 
            ...{ body } 
          })
          .then((response) => response.text());
        }

        // 3. Wait for all operations and process the final cart state
        Promise.all([propertyAddsPromise, updatesPromise]).then(([addResults, updateResult]) => {
            state = updateResult || (Array.isArray(addResults) && addResults.length > 0 ? addResults[addResults.length - 1] : '');

            if (!state) return; 
            
            const parsedState = JSON.parse(state);
            this.renderSections(parsedState, ids);
            if (shouldOpenCartDrawer) {
              window.closeDuplinMobileProductInfo?.();
              document.querySelector('cart-drawer')?.open?.();
            }
            window.duplinPickupGuard?.invalidateCart();
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          }).catch((error) => {
            console.error("Cart operation failed:", error);
          }).finally(() => {
            if (state) {
              try {
                let parsedState = JSON.parse(state); 
                const productCard = this.closest('.product-card-wrapper');
                const quickAddBulk = productCard.querySelector('quick-add-bulk');
                if (!quickAddBulk) return;
                const quantityInput = quickAddBulk.querySelector('quantity-input');
                const plusBtn = quantityInput?.querySelector('button[name="plus"]');
                if (!productCard) return;
                
                const newVariantId = ids.toString(); 
                const inputEl = quantityInput?.querySelector('input.quantity__input');
                if (inputEl) {
                  const quantity = this.getQuantityFromState(parsedState, newVariantId);

                  
                  // UI Update logic (same as original)
                  inputEl.value = quantity;
                  inputEl.setAttribute('data-index', newVariantId);
                  inputEl.setAttribute('data-quantity-variant-id', newVariantId);
                  inputEl.setAttribute('data-target', `quantity-input-${newVariantId}`);
                  inputEl.setAttribute('data-cart-quantity', quantity);
                  inputEl.id = `Quantity-${newVariantId}`;
                  
                  const selectSwatch = productCard.querySelector('.visual-swatch.selected');
                  if (selectSwatch) {
                    let avilQty = selectSwatch.getAttribute('data-quantity');
                    let isCase = selectSwatch.title == 'Case';
                    let maxQuantity = avilQty;
                    let isUnavailable = false;

                    if (isCase) {
                      maxQuantity = Math.floor(avilQty / 12);
                    }
                    
                    inputEl.setAttribute('max', maxQuantity);
                    isUnavailable = quantity >= maxQuantity;
                    
                    if (plusBtn) {
                      if(isUnavailable) {
                        plusBtn.classList.add('disabled');
                        plusBtn.setAttribute('disabled', 'disabled');
                      } else {
                        plusBtn.classList.remove('disabled');
                        plusBtn.removeAttribute('disabled');
                      }
                    }
                  } else if (plusBtn) {
                  const maxValue = parseInt(inputEl.getAttribute('max') || '', 10);
                  if (!Number.isNaN(maxValue) && quantity >= maxValue) {
                    plusBtn.classList.add('disabled');
                    plusBtn.setAttribute('disabled', 'disabled');
                  } else {
                    plusBtn.classList.remove('disabled');
                    plusBtn.removeAttribute('disabled');
                    } 
                  }
                }
              } catch (err) {
                console.error('Failed to parse state in finally', err);
              }
            }
            this.selectProgressBar().classList.add('hidden');
            this.setRequestStarted(false);
            this.animateCartCountBubble();
          });
      }
// --- END: CLASS/COMPONENT METHODS ---

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        // const intersection = this.queue.filter((element) => ids.includes(element.id));
        // if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (section.section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.length === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }

      animateCartCountBubble() {
        const bubble = document.querySelector('.cart-count-bubble');
        if (!bubble) return;
        bubble.classList.add('cart-updated');
        setTimeout(() => {
          bubble.classList.remove('cart-updated');
        }, 2000);
      }
    }
  );
}
